import { z } from "zod";
import type { DefectReportPayload } from "./defect-context";

const FIBERY_PUBLIC_FORM_KEY = "W1YC7klj";

const FIBERY_FIELD_IDS = {
	name: "f70e3a08-f109-4d4c-bc49-3fe652799219",
	description: "cffb0e09-4e99-409f-a6a2-a209a52921be",
	files: "673271b2-b6ba-443e-9b4d-60188fcded6d",
	stepsToReproduce: "244ad3c5-ec7c-4b4f-b9ac-cbe3e2f54f26",
	reporterName: "b5514c55-cd0a-408e-81c7-7df63ece48ee",
} as const;

const fiberyTempFileResponseSchema = z.object({
	"fibery/id": z.string(),
});

const fiberyRpcResponseSchema = z.object({
	jsonrpc: z.string(),
	result: z.unknown().optional(),
	error: z.unknown().optional(),
});

interface ProseMirrorMark {
	type: "link";
	attrs: {
		href: string;
		title: null;
	};
}

interface ProseMirrorTextNode {
	type: "text";
	text: string;
	marks?: ProseMirrorMark[];
}

interface ProseMirrorParagraph {
	type: "paragraph";
	attrs: {
		id: string;
		guid: string;
	};
	content: ProseMirrorTextNode[];
}

function createNodeGuid(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return `node-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createParagraph(nodes: ProseMirrorTextNode[]): ProseMirrorParagraph {
	const id = createNodeGuid();
	return {
		type: "paragraph",
		attrs: { id, guid: id },
		content: nodes.length > 0 ? nodes : [{ type: "text", text: " " }],
	};
}

function createLabeledLinkParagraph(
	label: string,
	url: string | null,
): ProseMirrorParagraph {
	if (!url) {
		return createParagraph([{ type: "text", text: `${label}: Not available` }]);
	}
	return createParagraph([
		{ type: "text", text: `${label}: ` },
		{
			type: "text",
			text: url,
			marks: [{ type: "link", attrs: { href: url, title: null } }],
		},
	]);
}

function serializeProseMirrorDoc(paragraphs: ProseMirrorParagraph[]): string {
	return JSON.stringify({
		doc: {
			type: "doc",
			content: paragraphs,
		},
		comments: [],
	});
}

export function buildFiberyDefectTitle(payload: DefectReportPayload): string {
	const firstLine = payload.note
		.split(/\r?\n/)
		.map((line) => line.trim())
		.find(Boolean);
	const rawSummary =
		firstLine && firstLine.length > 0
			? firstLine.slice(0, 90)
			: `Issue reported on ${payload.pathname || "/"}`;
	return rawSummary.startsWith("[USER]") ? rawSummary : `[USER] ${rawSummary}`;
}

export function buildFiberyFormValues(
	payload: DefectReportPayload,
	uploadedFileIds: string[] = [],
): Record<string, unknown> {
	const noteText =
		payload.note || "Reported via in-app Defect Recorder (no written note).";

	const descriptionParagraphs: ProseMirrorParagraph[] = [
		createParagraph([{ type: "text", text: `What happened: ${noteText}` }]),
		createLabeledLinkParagraph(
			"PostHog Session Replay (last 60s)",
			payload.posthog.replayUrl,
		),
		createLabeledLinkParagraph(
			"Sentry Session Replay",
			payload.sentry.replayUrl,
		),
		createLabeledLinkParagraph(
			"Sentry Feedback & Attachments",
			payload.sentry.feedbackUrl,
		),
		createLabeledLinkParagraph("Page URL", payload.url),
		createParagraph([
			{
				type: "text",
				text: [
					`Ref: #${payload.reportId.slice(0, 8)}`,
					`Report ID: ${payload.reportId}`,
					`Environment: ${payload.environment}`,
					`Client: ${payload.client}`,
					`Version: ${payload.appVersion}`,
					`Board ID: ${payload.boardId ?? "none"}`,
					`Card ID: ${payload.cardId ?? "none"}`,
					`Occurred At: ${payload.occurredAt}`,
				].join(" | "),
			},
		]),
	];

	if (payload.attachments.length > 0) {
		const attachmentList = payload.attachments
			.map((item) => `${item.name} (${Math.ceil(item.sizeBytes / 1024)} KB)`)
			.join(", ");
		descriptionParagraphs.push(
			createParagraph([
				{ type: "text", text: `Attached media: ${attachmentList}` },
			]),
		);
	}

	const recentErrorLines = payload.diagnostics.errorEntries
		.slice(-10)
		.map((err) => `[ERROR ${err.timestamp}] (${err.kind}) ${err.message}`);
	const recentConsoleLines = payload.diagnostics.consoleEntries
		.slice(-20)
		.map(
			(entry) =>
				`[${entry.level.toUpperCase()} ${entry.timestamp}] ${entry.message}`,
		);

	const diagnosticLines = [
		`Captured last 60s: ${payload.diagnostics.consoleEntries.length} console logs, ${payload.diagnostics.errorEntries.length} errors.`,
		...recentErrorLines,
		...recentConsoleLines,
	];

	const stepsParagraphs = diagnosticLines.map((line) =>
		createParagraph([{ type: "text", text: line }]),
	);

	const reporterName = payload.user
		? `${payload.user.displayName || payload.user.email} (${payload.user.email})`
		: "Anonymous User";

	const formValues: Record<string, unknown> = {
		[FIBERY_FIELD_IDS.name]: buildFiberyDefectTitle(payload),
		[FIBERY_FIELD_IDS.description]: serializeProseMirrorDoc(
			descriptionParagraphs,
		),
		[FIBERY_FIELD_IDS.stepsToReproduce]:
			serializeProseMirrorDoc(stepsParagraphs),
		[FIBERY_FIELD_IDS.reporterName]: reporterName,
	};

	if (uploadedFileIds.length > 0) {
		formValues[FIBERY_FIELD_IDS.files] = uploadedFileIds.map((id) => ({ id }));
	}

	return formValues;
}

function getProxyCandidateBaseUrls(): string[] {
	const candidates: string[] = ["/fibery-public-form"];
	const posthogHost = String(
		import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "",
	).replace(/\/+$/, "");
	if (posthogHost.startsWith("https://pulse.")) {
		const pulseProxyUrl = `${posthogHost}/fibery-public-form`;
		if (import.meta.env.DEV) {
			candidates.push(pulseProxyUrl);
		} else {
			candidates.unshift(pulseProxyUrl);
		}
	}
	return candidates;
}

async function uploadTempFileToFibery(
	baseUrl: string,
	file: File | Blob,
	filename: string,
): Promise<string | null> {
	try {
		const formData = new FormData();
		formData.append("file", file, filename);

		const response = await fetch(
			`${baseUrl}/api/files/temporary?form-key=${FIBERY_PUBLIC_FORM_KEY}`,
			{
				method: "POST",
				body: formData,
			},
		);
		if (!response.ok) {
			return null;
		}
		const raw: unknown = await response.json();
		const parsed = fiberyTempFileResponseSchema.safeParse(raw);
		return parsed.success ? parsed.data["fibery/id"] : null;
	} catch {
		return null;
	}
}

async function submitToFiberyBaseUrl(
	baseUrl: string,
	payload: DefectReportPayload,
	files: File[],
): Promise<boolean> {
	try {
		const uploadedFileIds: string[] = [];

		for (const file of files) {
			const id = await uploadTempFileToFibery(baseUrl, file, file.name);
			if (id) {
				uploadedFileIds.push(id);
			}
		}

		const diagnosticsBlob = new Blob([JSON.stringify(payload, null, 2)], {
			type: "application/json",
		});
		const diagnosticsFileId = await uploadTempFileToFibery(
			baseUrl,
			diagnosticsBlob,
			`defect-diagnostics-${payload.reportId.slice(0, 8)}.json`,
		);
		if (diagnosticsFileId) {
			uploadedFileIds.push(diagnosticsFileId);
		}

		const formValues = buildFiberyFormValues(payload, uploadedFileIds);
		const rpcBody = {
			jsonrpc: "2.0",
			method: "submit-form",
			params: {
				formKey: FIBERY_PUBLIC_FORM_KEY,
				formValues,
			},
			id: payload.reportId,
		};

		const response = await fetch(
			`${baseUrl}/api/forms/json-rpc?m=submit-form;formKey=${FIBERY_PUBLIC_FORM_KEY}&form-key=${FIBERY_PUBLIC_FORM_KEY}`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(rpcBody),
			},
		);
		if (!response.ok) {
			return false;
		}
		const raw: unknown = await response.json();
		const parsed = fiberyRpcResponseSchema.safeParse(raw);
		return Boolean(parsed.success && !parsed.data.error);
	} catch {
		return false;
	}
}

export async function submitFiberyDefect(
	payload: DefectReportPayload,
	files: File[] = [],
): Promise<boolean> {
	const baseUrls = getProxyCandidateBaseUrls();
	for (const baseUrl of baseUrls) {
		const ok = await submitToFiberyBaseUrl(baseUrl, payload, files);
		if (ok) {
			return true;
		}
	}
	return false;
}
